'use client'

import { ReactNode } from 'react'
import { useAuth, AuthTier } from '@/contexts/AuthContext'
import { UpgradePrompt } from './UpgradePrompt'

interface TierGateProps {
  children: ReactNode
  feature: string
  fallback?: ReactNode
  showPrompt?: boolean
}

export function TierGate({
  children,
  feature,
  fallback,
  showPrompt = true,
}: TierGateProps) {
  const { upgradeRequired, getTierForFeature } = useAuth()

  const requiredTier = upgradeRequired(feature)

  // User has access
  if (!requiredTier) {
    return <>{children}</>
  }

  // Show upgrade prompt
  if (showPrompt) {
    return (
      <UpgradePrompt
        feature={feature}
        requiredTier={requiredTier}
        variant="inline"
      />
    )
  }

  // Show fallback or nothing
  return fallback ? <>{fallback}</> : null
}

interface TierBadgeProps {
  tier: AuthTier
  size?: 'sm' | 'md'
}

export function TierBadge({ tier, size = 'sm' }: TierBadgeProps) {
  const colors: Record<string, string> = {
    free: 'bg-text-tertiary/10 text-text-tertiary',
    collector: 'bg-accent-warm/10 text-accent-warm',
    gallery: 'bg-accent-primary/10 text-accent-primary',
    enterprise: 'bg-accent-deep/10 text-accent-deep',
    individual: 'bg-accent-primary/10 text-accent-primary',
    institutional: 'bg-accent-deep/10 text-accent-deep',
  }

  const labels: Record<string, string> = {
    free: 'Free',
    collector: 'Collector',
    gallery: 'Gallery',
    enterprise: 'Enterprise',
    individual: 'Individual',
    institutional: 'Institutional',
  }

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${colors[tier]} ${
        size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1'
      }`}
    >
      {labels[tier]}
    </span>
  )
}
